// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/components/OpportunityList.tsx (ADAPT)
// ADAPTED (plan P4-2, P4-6, H3): the opportunity of each check is the BFF's (`vm.opportunityFor`, TP's text as the
// fallback) and shows the points it would gain. Not ported: `ShotPair` and `FUNNEL_SHOTS` (TP's Shapermint / Honeylove
// sample captures, no data of ours), the `agentPrompt` and the lever's "See their offers" link (both were "Not wired in the
// prototype yet"). The agent action is shown but disabled with the planning hint: nothing starts before signup (H3).
/**
 * A dimension's checks as opportunities, worst first, the first one open. Each one says what we see, the lever (with the
 * brands already pulling it), and the agent action that would start the work.
 */
import { Sparkles } from 'lucide-react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Button } from '@/components/ui/shadcn/button'
import { Card } from '@/components/ui/shadcn/card'
import { ASSESSMENT_COPY } from '../assessment-copy'
import { checksWorstFirst } from '../growth-score-model'
import type { Dimension } from '../growth-score.types'
import { useGrowthScore } from '../GrowthScoreContext'
import { BrandFaces, DimensionDot, ScorePill } from './visuals'

export function OpportunityList({ dimension }: { dimension: Dimension }) {
  const checks = checksWorstFirst(dimension.checks)
  return (
    <Card className="overflow-hidden px-5">
      <Accordion type="multiple" defaultValue={checks[0] ? [checks[0].key] : []}>
        {checks.map((check, i) => (
          <Opportunity key={check.key} dimension={dimension} checkKey={check.key} last={i === checks.length - 1} />
        ))}
      </Accordion>
    </Card>
  )
}

function Opportunity({ dimension, checkKey, last }: { dimension: Dimension; checkKey: string; last: boolean }) {
  const vm = useGrowthScore()
  const check = dimension.checks.find((c) => c.key === checkKey)!
  const { opportunity: o, pointsGained } = vm.opportunityFor(check.key)
  const faces = (o.lever?.alreadyDoing ?? []).map((name) => ({ name, logoUrl: vm.competitors.find((c) => c.name === name)?.logoUrl ?? null }))

  return (
    <AccordionItem value={check.key} className={last ? 'border-b-0' : 'border-ds-hairline'}>
      <AccordionTrigger className="gap-3 py-3.5 hover:no-underline">
        <span className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <DimensionDot dimension={dimension.key} />
          <span className="shrink-0 text-tab text-foreground">{check.name}</span>
          <span className="min-w-0 truncate text-body-sm text-ds-text-2">{o.summary}</span>
        </span>
        <span className="ml-auto shrink-0">
          <ScorePill score={check.score} label={check.label} />
        </span>
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-4 pb-5 pl-5">
        <div className="flex flex-col gap-1.5">
          <span className="text-caption text-ds-text-3">What it means</span>
          <p className="text-body-sm text-foreground">{o.whatWeSee}</p>
        </div>
        {o.lever ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-caption text-ds-text-3">The lever</span>
            <p className="text-tab text-foreground">{o.lever.title}</p>
            {o.lever.body ? <p className="text-body-sm text-ds-text-2">{o.lever.body}</p> : null}
            {pointsGained !== null ? <p className="text-label text-ds-text-2">Worth about +{pointsGained} points on this check.</p> : null}
            {faces.length ? (
              <div className="flex items-center gap-2 pt-1">
                <BrandFaces brands={faces} size="sm" label={`Already doing it: ${faces.map((f) => f.name).join(', ')}`} />
                <span className="text-label text-ds-text-2">Already doing it: {faces.map((f) => f.name).join(', ')}</span>
              </div>
            ) : null}
          </div>
        ) : null}
        {o.agent ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" disabled>
              <Sparkles aria-hidden />
              {o.agent.label}
            </Button>
            <span className="text-label text-ds-text-3">
              {o.agent.description} {ASSESSMENT_COPY.planningAfterSignup}
            </span>
          </div>
        ) : null}
      </AccordionContent>
    </AccordionItem>
  )
}
